import React, { use, useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { UserProfileInfoContainer } from "./styled";
import Icon from "components/Icon/Icon";
import {
  ArrowLeftIcon,
  BriefcaseIcon,
  PencilSimpleLineIcon,
  DotIcon,
  CaretDownIcon
} from "@phosphor-icons/react";
import Button from "components/Buttons";
import { ProfileOverview } from "./ProfileOverview";
import { EditProfileInfo } from "./EditProfileInfo";

import { selectUser } from "store/auth/slice/selector";
import { useDispatch, useSelector } from "react-redux";
import { useUsersSlice } from "pages/Users/slice/index";
import {
  selectMarketUser,
  selectUpdateUserInfo
} from "pages/Users/slice/selectors";
import PillButtons from "components/Buttons/PillButtons";
import { infoOptions, STATUS_COLORS } from "pages/Users/data";
import UserActivateModal from "pages/Users/Modals/UserActivateModal/index";

const UserProfileInfo = () => {
  const { user } = useSelector(selectUser);
  const { data: marketUser } = useSelector(selectMarketUser);
  const { loading: updateUserLoading, success } =
    useSelector(selectUpdateUserInfo);

  const { actions: userActions } = useUsersSlice();
  const dispatch = useDispatch();

  const { userId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const viewType = searchParams.get("mode") || "view";

  const [userStatus, setUserStatus] = useState("");
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [currentStatus, setCurrentStatus] = useState("");

  const [editInformation, setEditInformation] = useState({
    phone_number: "",
    country_code: "",
    email: "",
    company: "",
    first_name: "",
    last_name: "",
    designation: "",
    linkedin_url: "",
    profile_image: ""
  });

  const filteredInfoOptions = React.useMemo(() => {
    if (!marketUser?.status) return infoOptions;

    return infoOptions.filter((option) => {
      return (
        option.apiKey === marketUser.status ||
        option.apiKey === "activated" ||
        option.apiKey === "deactivated"
      );
    });
  }, [marketUser?.status]);

  useEffect(() => {
    if (marketUser) {
      const statusOption = infoOptions.find(
        (option) => option.apiKey === marketUser.status
      );
      setUserStatus(statusOption);
    }
  }, [marketUser]);

  useEffect(() => {
    dispatch(
      userActions.fetchUserById({
        userId: userId
      })
    );
  }, [userId]);

  const updateUserInfo = () => {
    dispatch(
      userActions.userUpdate({
        user_id: marketUser.id,
        data: {
          first_name: editInformation.first_name,
          last_name: editInformation.last_name,
          designation: editInformation.designation,
          linkedin_url: editInformation.linkedin_url,
          profile_image: editInformation.profile_image
        }
      })
    );
  };

  useEffect(() => {
    if (success) {
      navigate(
        {
          pathname: `/marketer-users/${marketUser.id}`,
          search: "?mode=view"
        },
        { replace: true }
      );
    }
  }, [success]);

  return (
    <UserProfileInfoContainer>
      <div className="user-info-header">
        <div className="header-left-section">
          <figure
            onClick={() =>
              navigate(
                viewType === "edit"
                  ? `/marketer-users/${marketUser.id}?mode=view&${searchParams.toString()}`
                  : `/marketer-users?${searchParams.toString()}`
              )
            }
          >
            <Icon component={ArrowLeftIcon} />
          </figure>
          <h2>
            {viewType === "edit"
              ? `Edit ${marketUser?.first_name ? marketUser?.first_name : "Details"}`
              : `${marketUser?.first_name ? marketUser?.first_name : "Profile Details"}`}
          </h2>
          <div className="user-role-badge">
            <Icon component={BriefcaseIcon} color="#EC6B6F" />
            <span>Marketer User</span>
          </div>
        </div>

        {viewType === "view" ? (
          <Button
            size="M"
            variant="primary"
            leftIcon={<Icon component={PencilSimpleLineIcon} color="#FFFFFF" />}
            onClick={() =>
              navigate(
                {
                  pathname: `/marketer-users/${marketUser.id}`,
                  search: "?mode=edit"
                },
                { replace: false }
              )
            }
          >
            Edit Details
          </Button>
        ) : (
          <div className="update-user-dropdown">
            <PillButtons
              statusPlaceholder="statusActive"
              mainLabelText={userStatus?.label}
              leftIcon={
                <Icon
                  component={DotIcon}
                  size={12}
                  color={STATUS_COLORS[userStatus?.apiKey] || "#000000"}
                />
              }
              rightIcon={
                <Icon
                  component={CaretDownIcon}
                  size={12}
                  color={STATUS_COLORS[userStatus?.apiKey] || "#000000"}
                />
              }
              menuItems={filteredInfoOptions}
              size="M"
              onSelect={(item) => {
                if (marketUser.status === item.apiKey) return;
                setShowActivateModal(true);
                setCurrentStatus(item.apiKey);
              }}
              color={STATUS_COLORS[userStatus?.apiKey] || "#000000"}
              isShowbackground={true}
            />
          </div>
        )}
      </div>

      <div
        className={`profile-information-container ${viewType === "edit" ? "edit-mode" : ""}`}
      >
        {viewType === "view" ? (
          <ProfileOverview marketUserDetails={marketUser} />
        ) : (
          <EditProfileInfo
            marketUserDetails={marketUser}
            setEditInformation={setEditInformation}
            editInformation={editInformation}
          />
        )}
      </div>

      {viewType === "edit" && (
        <div className="profile-edit-footer">
          <Button
            variant="secondary"
            onClick={() =>
              navigate(`/marketer-users/${marketUser.id}?mode=view`)
            }
          >
            Cancel
          </Button>
          <Button
            size="M"
            variant="primary"
            onClick={updateUserInfo}
            loading={updateUserLoading}
          >
            Save Changes
          </Button>
        </div>
      )}

      <UserActivateModal
        show={showActivateModal}
        onHide={() => setShowActivateModal(false)}
        userInfo={marketUser}
        currentStatus={currentStatus}
      />
    </UserProfileInfoContainer>
  );
};

export default UserProfileInfo;
